import { useEffect, type CSSProperties, type ReactElement } from 'react';
import { Icon } from '@iconify/react';
import { File as FileIcon, Folder, FolderOpen, type LucideIcon } from 'lucide-react';
import { iconNameForFile, iconNameForFolder } from './fileIconName';
import { ensureIconSet, hasIcon, useIconSetReady } from './registerIconSet';

export interface FileTypeIconProps {
  /** File or folder basename (e.g. `app.ts`, `src`). */
  name: string;
  /** Whether to resolve a file icon or a folder icon. */
  kind: 'file' | 'folder';
  /** Folders only: render the opened variant. Ignored for files. */
  open?: boolean;
  /** Pixel size (width and height). Defaults to 14 to match the tree rows. */
  size?: number;
  /** Reduced opacity for unreadable/binary files. */
  muted?: boolean;
  /** Forwarded to the rendered element. */
  className?: string;
}

/**
 * One icon for every filename surface (file tree, sidebar search, open-doc
 * tabs). Renders the bundled vscode-icons glyph once the set is registered;
 * until then (or for an unmapped name) it renders the matching Lucide glyph so
 * a row is never blank. Decorative: always `aria-hidden`.
 */
export function FileTypeIcon({
  name,
  kind,
  open = false,
  size = 14,
  muted = false,
  className,
}: FileTypeIconProps): ReactElement {
  const ready = useIconSetReady();
  useEffect(() => {
    ensureIconSet();
  }, []);

  const iconName =
    kind === 'folder' ? iconNameForFolder(name, open) : iconNameForFile(name);
  const style: CSSProperties | undefined = muted ? { opacity: 0.55 } : undefined;

  if (ready && hasIcon(iconName)) {
    return (
      <Icon
        icon={iconName}
        width={size}
        height={size}
        className={className}
        style={style}
        aria-hidden
      />
    );
  }

  const Fallback: LucideIcon =
    kind === 'folder' ? (open ? FolderOpen : Folder) : FileIcon;
  return <Fallback size={size} className={className} style={style} aria-hidden />;
}
